import { guestApiPath } from '../guestApiPath';
import { getActivePolicySet, getPolicy } from '../legal/policyRegistry';
import { decodeJwtPayload } from './decodeJwtPayload';
import { getGuestAuthToken } from './guestToken';

export const GUEST_UPLOAD_NOTICE_ACKNOWLEDGED = 'wedlinx:guest-upload-notice-acknowledged';
export const GUEST_UPLOAD_NOTICE_CHANGED = 'wedlinx:guest-upload-notice-changed';
const memory = new Map();
const serverStates = new Map();
const statusRequests = new Map();
const statusRevisions = new Map();

export function guestNoticeSession(galleryId, token = getGuestAuthToken()) {
  const claims = decodeJwtPayload(token);
  if (!claims || claims.purpose === 'preview' || claims.galleryId !== galleryId
    || !Number.isFinite(claims.exp) || claims.exp * 1000 <= Date.now()) return null;
  // Current sessions have a random jti. The signature identifies legacy signed sessions
  // without copying an entire bearer credential into acknowledgement storage.
  const sessionId = claims.jti || token.split('.')[2];
  return sessionId ? { galleryId, sessionId, expiresAt: claims.exp * 1000 } : null;
}

const storageKey = (session, version) => `wedlinx-upload-notice:${encodeURIComponent(session.galleryId)}:${encodeURIComponent(session.sessionId)}:${encodeURIComponent(version)}`;
const sessionKey = (session) => storageKey(session, '');
const valid = (record, session, version) => Boolean(session) && record?.acknowledged === true
  && record.galleryId === session?.galleryId && record.sessionId === session?.sessionId
  && record.version === version && Number.isFinite(record.confirmedAt);

export function readGuestUploadAcknowledgement(galleryId, version, token = getGuestAuthToken()) {
  const session = guestNoticeSession(galleryId, token);
  if (!session) return null;
  const key = storageKey(session, version);
  try {
    const stored = JSON.parse(localStorage.getItem(key));
    if (valid(stored, session, version)) return stored;
  } catch { /* The in-memory record still supports a confirmed session if storage is unavailable. */ }
  const record = memory.get(key);
  return valid(record, session, version) ? record : null;
}

function saveAcknowledgement(record) {
  const key = storageKey(record, record.version);
  memory.set(key, record);
  try { localStorage.setItem(key, JSON.stringify(record)); } catch { /* Queue metadata also carries genuine offline confirmation. */ }
  return record;
}

function announce(galleryId, token) {
  if (token === getGuestAuthToken()) {
    window.dispatchEvent(new CustomEvent(GUEST_UPLOAD_NOTICE_CHANGED, { detail: { galleryId } }));
  }
}

function hasNoticeDocuments(active) {
  const local = getActivePolicySet();
  if (local?.guestNoticeVersion === active.guestNoticeVersion
    && local.versions?.terms === active.versions.terms && local.versions?.privacy === active.versions.privacy) return true;
  return ['terms', 'privacy'].every((id) => active.versions[id]
    && getPolicy(id, active.versions[id])?.status === 'published');
}

function noticeState(galleryId, token, isPreview = false) {
  if (isPreview || decodeJwtPayload(token)?.purpose === 'preview') {
    return { required: false, acknowledged: false, active: null, session: null };
  }
  const session = guestNoticeSession(galleryId, token);
  const server = session && serverStates.get(sessionKey(session));
  // An authenticated server response overrides the device clock. Local policy dates
  // remain the fallback before the first successful sync, including while offline.
  const active = server ? server.active : getActivePolicySet();
  return { required: Boolean(active), active, session,
    updateRequired: Boolean(server && active && !hasNoticeDocuments(active)),
    acknowledged: Boolean(active && readGuestUploadAcknowledgement(galleryId, active.guestNoticeVersion, token)) };
}

export function currentGuestUploadNotice(galleryId, isPreview = false) {
  return noticeState(galleryId, getGuestAuthToken(), isPreview);
}

// Only call from the notice's explicit confirmation action. Merely displaying it, selecting
// files, or receiving a new guest token must never create an acknowledgement.
export function confirmGuestUploadNotice(galleryId, displayedNotice) {
  const { active, session, updateRequired } = currentGuestUploadNotice(galleryId);
  if (!active || !session) throw new Error('Sign in to this gallery before confirming the upload notice.');
  if (updateRequired) throw new Error('Update Wedlinx to read the current upload notice before confirming it.');
  if (displayedNotice && (displayedNotice.version !== active.guestNoticeVersion
    || displayedNotice.sessionId !== session.sessionId)) {
    throw new Error('The notice or your guest session changed. Read the current notice and confirm again.');
  }
  const existing = readGuestUploadAcknowledgement(galleryId, active.guestNoticeVersion);
  const record = existing || saveAcknowledgement({
    ...session, version: active.guestNoticeVersion, acknowledged: true, confirmedAt: Date.now(), serverRecorded: false,
  });
  window.dispatchEvent(new CustomEvent(GUEST_UPLOAD_NOTICE_ACKNOWLEDGED, { detail: { galleryId } }));
  return record;
}

function noticeRequired() {
  const error = new Error('Read and confirm the upload notice to continue your saved uploads.');
  error.response = { status: 428, data: { error: 'UPLOAD_NOTICE_REQUIRED', message: error.message } };
  return error;
}

function statusState(data) {
  if (data?.required === false) return { active: null };
  if (data?.required !== true || typeof data.version !== 'string' || !data.version
    || !['terms', 'privacy'].every((id) => typeof data.versions?.[id] === 'string' && data.versions[id])) {
    throw new Error('The current upload notice is temporarily unavailable. Please try again.');
  }
  return { active: { guestNoticeVersion: data.version, versions: { ...data.versions } } };
}

// Coalesce concurrent lookups for a verified session. A forced refresh (after a 428)
// supersedes older responses, so a slow request cannot restore an obsolete version.
export async function syncGuestUploadNotice(galleryId, token = getGuestAuthToken(), { force = false } = {}) {
  const session = guestNoticeSession(galleryId, token);
  if (!session || navigator.onLine === false) return null;
  const key = sessionKey(session);
  if (!force && statusRequests.has(key)) return statusRequests.get(key);
  const revision = (statusRevisions.get(key) || 0) + 1;
  statusRevisions.set(key, revision);
  const operation = (async () => {
    const api = (await import('../apiClient')).default;
    const response = await api.get(guestApiPath('/upload-notice', galleryId), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const state = statusState(response.data);
    if (statusRevisions.get(key) !== revision) return null;
    const previous = serverStates.get(key);
    serverStates.set(key, state);
    let record = null;
    let restored = false;
    const confirmedAt = Date.parse(response.data.acknowledgedAt);
    if (state.active && response.data.acknowledged === true && Number.isFinite(confirmedAt)) {
      const existing = readGuestUploadAcknowledgement(galleryId, state.active.guestNoticeVersion, token);
      record = saveAcknowledgement({ ...session, version: state.active.guestNoticeVersion,
        acknowledged: true, confirmedAt, serverRecorded: true });
      restored = !existing?.serverRecorded || existing.confirmedAt !== confirmedAt;
    }
    if (JSON.stringify(previous) !== JSON.stringify(state) || restored) announce(galleryId, token);
    return { ...state, record };
  })();
  statusRequests.set(key, operation);
  try { return await operation; }
  finally { if (statusRequests.get(key) === operation) statusRequests.delete(key); }
}

// Queue items retain genuine offline confirmation across reloads. An acknowledgement from a
// different gallery, token/session or policy version is never replayed for the current session.
export async function ensureGuestUploadAcknowledgement(galleryId, token, queuedRecord, signal) {
  if (decodeJwtPayload(token)?.purpose === 'preview') return null;
  const session = guestNoticeSession(galleryId, token);
  if (!session) throw noticeRequired();
  // Always recheck online, even if a previous response or the local clock said no
  // notice was active. Signing independently enforces any change after this read.
  let server;
  try { server = await syncGuestUploadNotice(galleryId, token); }
  catch (error) {
    // During rollout an older API has no notice endpoint. Only an unknown/inactive
    // policy may fall through to its independently guarded upload-url endpoint.
    if (error.response?.status !== 404 || noticeState(galleryId, token).active || getActivePolicySet()) throw error;
  }
  if (signal?.aborted) throw Object.assign(new Error('Upload cancelled'), { name: 'AbortError' });
  const { active } = server || noticeState(galleryId, token);
  if (!active) return null;
  if (server?.record) return server.record;
  let record = readGuestUploadAcknowledgement(galleryId, active.guestNoticeVersion, token);
  if (!record && valid(queuedRecord, session, active.guestNoticeVersion)) record = queuedRecord;
  if (!record) throw noticeRequired();
  const revision = statusRevisions.get(sessionKey(session));
  const api = (await import('../apiClient')).default;
  try {
    const response = await api.post(guestApiPath('/upload-notice', galleryId), {
      version: active.guestNoticeVersion, acknowledged: true,
    }, { headers: { Authorization: `Bearer ${token}` }, signal });
    if (response.data?.required === false) {
      if (statusRevisions.get(sessionKey(session)) !== revision) throw noticeRequired();
      serverStates.set(sessionKey(session), { active: null });
      announce(galleryId, token);
      return null;
    }
    if (response.data?.acknowledged !== true || response.data.version !== active.guestNoticeVersion) throw noticeRequired();
    // A login switch while delivery was pending must not acknowledge the replacement session.
    record = saveAcknowledgement({ ...record, serverRecorded: true });
  } catch (error) {
    if (error.response?.data?.error === 'UPLOAD_NOTICE_REQUIRED') {
      await resetGuestUploadNoticeDelivery(galleryId, token, error.response.data.version);
    }
    throw error;
  }
  return record;
}

export async function restoreGuestUploadAcknowledgement(galleryId, token = getGuestAuthToken()) {
  const state = await syncGuestUploadNotice(galleryId, token);
  return token === getGuestAuthToken() ? state?.record || null : null;
}

export async function resetGuestUploadNoticeDelivery(galleryId, token = getGuestAuthToken(), version) {
  const session = guestNoticeSession(galleryId, token);
  if (!session) return;
  if (version && version !== noticeState(galleryId, token).active?.guestNoticeVersion) {
    // Until the full status arrives, never let an older notice confirm this version.
    serverStates.set(sessionKey(session), { active: { guestNoticeVersion: version, versions: {} } });
    announce(galleryId, token);
  }
  try { await syncGuestUploadNotice(galleryId, token, { force: true }); }
  catch { /* Keep the version hint visible; reconnect/focus or retry will sync again. */ }
}
